import React, { useState } from "react";
import { Box, Typography, Stack, Divider, Link } from "@mui/material";
import { useNavigate } from "react-router-dom";
import SkillLevelBadge from "./SkillLevelBadge";
import SkillFrameworkList from "./SkillFrameworkList";
import UsedWorkHistoriesModal from "./UsedWorkHistoriesModal";
import { TEXT_COLOR_PRIMARY } from "../../../constants/colors";
import { editSkillSheetPathFor } from "../../utils/skillSheetRouting";

/**
 * スキル1行分の表示コンポーネント
 *
 * 表示内容：
 * - 名前
 * - 経験年数
 * - スキルレベル
 * - 紐づく子要素（存在する場合のみ）
 */
const SkillItemRow = ({
  parentSkill,
  childSkills = [],
  owner,
  categoryKey,
}) => {
  const navigate = useNavigate();
  // 使用した work_history 一覧の表示／非表示を管理
  const [openModal, setOpenModal] = useState(false);

  const hasChildren = childSkills.length > 0;

  // このスキルを使用した work_history が存在するかどうか
  const usedWorkHistories = parentSkill?.used_work_histories ?? [];
  const hasUsedWorkHistories = usedWorkHistories.length > 0;

  const isTool = categoryKey === "tool";

  // スキル名クリック時に、使用した work_history 一覧を開閉する
  const handleSkillClick = () => {
    if (!hasUsedWorkHistories) return;
    setOpenModal(true);
  };

  const handleClose = () => setOpenModal(false);

  // 選択した work_history の編集画面へ遷移する
  const handleWorkHistoryClick = (workHistoryId) => {
    setOpenModal(false);
    navigate(editSkillSheetPathFor(workHistoryId, owner));
  };

  return (
    <Box>
      {/* 基本情報（名前 / 経験年数 / レベル） */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: "8px",
        }}
      >
        {/* スキル名（クリックで使用プロジェクトを開閉） */}
        <Link
          component="button"
          type="button"
          onClick={handleSkillClick}
          disabled={!hasUsedWorkHistories}
          underline="none"
          sx={{
            fontWeight: "bold",
            cursor: hasUsedWorkHistories ? "pointer" : "default",
            color: "inherit",
            p: 0,
            background: "none",
            border: "none",
            textAlign: "left",
            "&:hover": {
              textDecoration: "underline",
              textDecorationColor: "#000",
            },
          }}
          aria-haspopup="dialog"
          aria-expanded={openModal ? "true" : "false"}
        >
          {parentSkill?.displayName}
        </Link>

        {/* 経験年数 + レベル */}
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography whiteSpace="nowrap" sx={{ color: TEXT_COLOR_PRIMARY }}>
            {parentSkill?.experienceLabel}
          </Typography>
          {/* Tool 以外 & レベルがある場合のみ表示 */}
          {parentSkill?.level && !isTool && (
            <SkillLevelBadge level={parentSkill.level} />
          )}
        </Stack>
      </Box>

      {/* モーダル（共通コンポーネント） */}
      <UsedWorkHistoriesModal
        open={openModal}
        onClose={handleClose}
        title={`${parentSkill?.displayName} を使用したプロジェクト`}
        workHistories={usedWorkHistories}
        onClickWorkHistory={handleWorkHistoryClick}
      />

      {/* 子要素がある場合のみ表示 */}
      {hasChildren && (
        <>
          <Divider
            sx={{
              my: 1.5,
              borderColor: "#E4E6F6",
            }}
          />

          <SkillFrameworkList frameworks={childSkills} owner={owner} />
        </>
      )}
    </Box>
  );
};

export default SkillItemRow;
